export const roundOff = (num, places = 2) => {
  if (typeof num !== 'number' || !Number.isFinite(num)) return 0;

  // If it's an integer, return as is
  if (Number.isInteger(num)) return num;

  // If it's a float, round to decimals & remove trailing zeros
  return parseFloat(parseInt(num * 10 ** places, 10) / 100);
};

export const toR = (rsi, roundR = false) => {
  const r = (rsi ?? 0) * 5.678;
  return roundR ? Math.round(r) : r;
};

export const toRSI = {
  12: 2.11,
  14: 2.46,
  19: 3.34,
  20: 3.52,
  22: 3.96,
  24: 4.23,
  28: 4.93,
  31: 5.46,
  35: 6.16,
  40: 7.04,
};

export const toRSIFloorsOverUnheatedSpace = {
  ...toRSI,
  22: 3.87,
};

export const AIR_FILM_EXTERIOR_RSI = 0.03;
export const AIR_FILM_INTERIOR_RSI = 0.12;
export const INTERIOR_GWB_RSI_PER_MM = 0.0063;
export const WOOD_STRAP_BEHIND_GWB_RSI = 0.15;

// Films & layers
export const OSB_RSI_PER_MM = 0.0087;
export const PLYWOOD_RSI_PER_MM = 0.0087;
export const CODEBORD_RSI_PER_INCH = 0.88;
export const RAINBARRIER_RSI_PER_INCH = 0.74;

export const RAINSCREEN_RSI = { '13-19': 0.16, '20-90': 0.18 };
export const BRICK_AIR_SPACE_RSI = { yes: 0.18, no: 0 };

export const ROOF_RSIF = { '2x4': 0.757, '2x6': 1.19 };

export const onCenterSpacingOptions = {
  304: '304 mm (12")',
  406: '406 mm (16")',
  488: '488 mm (19.2")',
  610: '610 mm (24")',
};

const CODEBORD_THICKNESS_FACTORS = {
  r: 5,
  rsi: 0.88,
};

const RAINBARRIER_THICKNESS_FACTORS = {
  r: 4.2,
  rsi: 0.74,
};

const thicknessOptions = type => {
  const factors = {
    codebord: CODEBORD_THICKNESS_FACTORS,
    rainbarrier: RAINBARRIER_THICKNESS_FACTORS,
  };
  const r = value =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(
      value * factors[type].r
    );
  const rsi = value =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 2 }).format(
      value * factors[type].rsi
    );

  return [
    { value: '1', text: `25.4 mm (1") - R-${r(1)}/RSI-${rsi(1)}` },
    {
      value: '1.5',
      text: `38.1 mm (1.5") - R-${r(1.5)}/RSI-${rsi(1.5)}`,
    },
    {
      value: '2',
      text: `50.8 mm (2") - R-${r(2)}/RSI-${rsi(2)}`,
    },
    {
      value: '2.5',
      text: `63.5 mm (2.5") - R-${r(2.5)}/RSI-${rsi(2.5)}`,
    },
    {
      value: '3',
      text: `76.2 mm (3") - R-${r(3)}/RSI-${rsi(3)}`,
    },
    {
      value: '3.5',
      text: `88.9 mm (3.5") - R-${r(3.5)}/RSI-${rsi(3.5)}`,
    },
    {
      value: '4',
      text: `101.6 mm (4") - R-${r(4)}/RSI-${rsi(4)}`,
    },
  ];
};

export const CODEBORD_THICKNESS_OPTIONS = thicknessOptions('codebord');

export const RAINBARRIER_THICKNESS_OPTIONS = thicknessOptions('rainbarrier');

export const FramingText = {
  '2x4': '38 mm x 89 mm (2"x4")',
  '2x6': '38 mm x 140 mm (2"x6")',
  '2x8': '38 mm x 184 mm (2"x8")',
  '2x10': '38 mm x 235 mm (2"x10")',
  '2x12': '38 mm x 286 mm (2"x12")',
};

export const validateInputs = ({ province, region, setError, cavityRSI, validateCavityRSI = true, t }) => {
  if (!province || !region) {
    setError(t('thermal_calculator.errors.select_province_region'));
    return false;
  }
  if (validateCavityRSI && !cavityRSI) {
    setError(t('thermal_calculator.errors.select_cavity'));
    return false;
  }
  setError(null);
  return true;
};

const generateRSIOptions = ({ values = [], rsiValues }) => {
  const options = [];
  values.forEach(r => {
    const roundedRSI = rsiValues[r];
    options.push({
      text: `RSI ${roundedRSI}, R ${r}`,
      value: roundedRSI,
    });
  });
  return options;
};

export const insulationOptions = (rsiRange, rsiValues = toRSI) =>
  Object.entries(rsiRange).reduce((acc, [key, data]) => {
    acc[key] = generateRSIOptions({ values: data.values, rsiValues });
    return acc;
  }, {});

export const resetResults = (calcId, assemblyName, publishResults) => {
  publishResults({ calcId, results: null, meta: { title: assemblyName } });
};
